/**
 * 三處共用的待付單提示彈窗：查詢（GET draw/hold）、渲染、取消（POST draw/cancel）。
 * DOM 掛載本身很薄，可測的判斷邏輯（要不要顯示取消鈕、剩餘秒數怎麼算）抽成純函式。
 */
import { formatPoints } from './draw-quote.mjs';
import { createClock } from './clock.mjs';
import { createRestClient } from './rest-client.mjs';

/**
 * 伺服器回傳的 hold 摘要轉成畫面用的 view model。hold 為 null 代表沒有待付單。
 * 出貨儲值單的摘要（ShipTopupHoldSummary::build()）沒有 remaining_seconds 這個 key
 * （決議 10：不設保留窗口），此時 hasCountdown 為 false，呼叫端據此隱藏倒數列。
 * 標題依摘要的 kind 切換；缺 kind 一律當抽獎單。
 * @param {?{order_id:number, product_name?:string, cash_amount:number, remaining_seconds?:number, payment_url:string, cancellable:boolean, kind?:string}} hold
 * @returns {{visible:boolean, title:string, orderId:?number, productName:string, cashAmountText:string, remainingSeconds:number, hasCountdown:boolean, paymentUrl:string, showCancelButton:boolean}}
 */
export function holdPopupModel(hold) {
    const drawTitle = '您有一張待付款的抽獎單';
    if (!hold) {
        return { visible: false, title: drawTitle, orderId: null, productName: '', cashAmountText: '', remainingSeconds: 0, hasCountdown: false, paymentUrl: '', showCancelButton: false };
    }

    return {
        visible: true,
        title: hold.kind === 'ship' ? '您有一張待付款的出貨儲值單' : drawTitle,
        orderId: hold.order_id,
        productName: hold.product_name || '',
        cashAmountText: 'NT$' + formatPoints(hold.cash_amount || 0),
        remainingSeconds: Math.max(0, hold.remaining_seconds || 0),
        hasCountdown: typeof hold.remaining_seconds === 'number',
        paymentUrl: hold.payment_url || '',
        showCancelButton: hold.cancellable === true,
    };
}

/**
 * 被擋的回應是否該開這個共用彈窗，而不是走一般 alert()。
 *
 * 認 draw_hold / ship_hold：兩者都是「查對應 hold 端點一定查得到摘要可顯示」的情境
 * （出貨儲值單查 fulfillment/topup/hold，見呼叫端如何依 code 決定要查哪個端點）。
 * draw_cooldown 不適用——冷卻成立的前提就是沒有 active 待付單（兩者互斥判定），此時查
 * draw/hold 必定回 null，彈窗會直接關閉、DrawBlockMessage::forCooldown() 的訊息就永遠
 * 顯示不出來；冷卻繼續走呼叫端原本的 alert()/toast，直接顯示 res.data.message。
 * @param {?{code?: string}} data 業務錯誤回應的 data payload
 * @returns {boolean}
 */
export function isHoldBlockedResponse(data) {
    return !!data && (data.code === 'draw_hold' || data.code === 'ship_hold');
}

/**
 * 商品頁被出貨儲值互斥擋下（`ship_hold`）時的提示。商品頁的共用彈窗只認抽獎待付單，
 * 出貨儲值單要到出貨頁處理：有出貨頁連結就問要不要前往，沒有就只顯示訊息。
 * @param {?{code?: string, message?: string}} data 業務錯誤回應的 data payload
 * @param {?string} readyToShipUrl 出貨頁連結
 * @returns {?{text: string, url: ?string}} 不是 `ship_hold` 時回 null
 */
export function shipHoldPrompt(data, readyToShipUrl) {
    if (!data || data.code !== 'ship_hold') {
        return null;
    }

    const message = data.message || '您有一張待付款的出貨儲值單，請先完成付款或取消後再試。';
    if (!readyToShipUrl) {
        return { text: message, url: null };
    }

    return { text: message + '\n\n前往出貨頁處理？', url: readyToShipUrl };
}

/**
 * 顯示 `shipHoldPrompt()` 的提示：有出貨頁連結就問要不要前往，確定即導頁；沒有連結只顯示訊息。
 * 一番賞與轉蛋商品頁被擋的處理都先走這支。
 * @param {?{code?: string, message?: string}} data 業務錯誤回應的 data payload
 * @param {?string} readyToShipUrl 出貨頁連結
 * @returns {boolean} true = 是 `ship_hold`、已顯示提示，呼叫端不必再 alert()
 */
export function showShipHoldPrompt(data, readyToShipUrl) {
    const prompt = shipHoldPrompt(data, readyToShipUrl);
    if (!prompt) {
        return false;
    }

    if (prompt.url) {
        if (window.confirm(prompt.text)) {
            window.location.href = prompt.url;
        }
    } else {
        window.alert(prompt.text);
    }
    return true;
}

/**
 * 取消結果分岔：成功取消 / 已收到付款轉開獎輪詢 / 不明狀態（含請求本身失敗）。
 * @param {boolean} success draw/cancel 回應的 success 欄位
 * @param {?{status?:string, poll?:boolean, message?:string}} data
 * @returns {{action: 'cancelled'|'poll'|'blocked', message: ?string}}
 */
export function cancelOutcome(success, data) {
    if (!success || !data) {
        return { action: 'blocked', message: (data && data.message) || null };
    }
    if (data.status === 'already_paid') {
        return { action: 'poll', message: data.message || null };
    }
    if (data.status === 'cancelled') {
        return { action: 'cancelled', message: null };
    }
    return { action: 'blocked', message: data.message || null };
}

/**
 * 查詢目前是否有待付抽獎單。sentAt/receivedAt 供呼叫端交給 clock.sync() 做 RTT 校正
 * （比照 lottery_product_single_page.js 既有的 fetchSnapshot() 對時方式），不要把
 * 兩者都填 Date.now() 傳給 sync()，那樣整段 RTT 會被算進 offset。
 * @param {string} restBase
 * @param {string} nonce
 * @param {string} [path] 待付單摘要端點，預設抽獎的 `/draw/hold`；出貨儲值單呼叫端傳
 *     `/fulfillment/topup/hold`
 * @returns {Promise<{hold: ?object, serverTime: ?number, sentAt: number, receivedAt: number}>}
 */
export async function fetchHold(restBase, nonce, path = '/draw/hold') {
    const client = createRestClient({ restBase, nonce });
    const sentAt = Date.now();
    const json = await client.get(path);
    const receivedAt = Date.now();
    if (!json.success) {
        return { hold: null, serverTime: null, sentAt, receivedAt };
    }
    return {
        hold: (json.data && json.data.hold) || null,
        serverTime: (json.data && json.data.server_time) || null,
        sentAt,
        receivedAt,
    };
}

/**
 * 取消待付單。
 * @param {string} restBase
 * @param {string} nonce
 * @param {number} orderId
 * @param {string} [path] 取消端點，預設抽獎的 `/draw/cancel`；出貨儲值單呼叫端傳
 *     `/fulfillment/topup/cancel`
 * @returns {Promise<{success:boolean, data:object}>}
 */
export async function requestCancel(restBase, nonce, orderId, path = '/draw/cancel') {
    const client = createRestClient({ restBase, nonce });
    return client.post(path, { order_id: orderId });
}

/**
 * 掛載共用彈窗：綁定 DOM 節點、倒數顯示、取消鈕。回傳 { open(hold, serverTime), close() }
 * 供呼叫端在被擋的 AJAX 回應觸發時開啟。DOM 結構見 templates/partials/draw-hold-popup.php。
 *
 * @param {{root: Element, restBase: string, nonce: string, onCancelled?: function, onPoll?: function}} opts
 */
export function mountDrawHoldPopup(opts) {
    const { root, restBase, nonce, onCancelled, onPoll } = opts;
    if (!root) {
        return { open() {}, close() {} };
    }

    const els = {
        title: root.querySelector('[data-wtoy-hold-title]'),
        productName: root.querySelector('[data-wtoy-hold-product]'),
        cashAmount: root.querySelector('[data-wtoy-hold-amount]'),
        remaining: root.querySelector('[data-wtoy-hold-remaining]'),
        payLink: root.querySelector('[data-wtoy-hold-pay]'),
        cancelBtn: root.querySelector('[data-wtoy-hold-cancel]'),
        closeBtn: root.querySelector('[data-wtoy-hold-close]'),
    };

    const clock = createClock();
    let countdownTimer = null;
    let currentOrderId = null;
    let currentCancelPath = '/draw/cancel';

    function stopCountdown() {
        if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
        }
    }

    function close() {
        stopCountdown();
        root.classList.remove('open');
    }

    function renderRemaining(expiresAtMs) {
        const seconds = clock.remainingSeconds(expiresAtMs, Date.now());
        if (els.remaining) {
            els.remaining.textContent = String(seconds);
        }
        if (seconds <= 0) {
            stopCountdown();
        }
    }

    /**
     * @param {?object} hold
     * @param {?number} serverTime fetchHold() 回的 server_time（epoch 秒）
     * @param {?number} sentAtMs fetchHold() 送出請求當下的 Date.now()
     * @param {?number} receivedAtMs fetchHold() 收到回應當下的 Date.now()
     * @param {string} [cancelPath] 取消鈕要打的端點，預設 `/draw/cancel`；出貨儲值單
     *     呼叫端傳 `/fulfillment/topup/cancel`
     */
    function open(hold, serverTime, sentAtMs, receivedAtMs, cancelPath = '/draw/cancel') {
        const model = holdPopupModel(hold);
        if (!model.visible) {
            close();
            return;
        }

        if (typeof serverTime === 'number') {
            clock.sync(sentAtMs ?? Date.now(), receivedAtMs ?? Date.now(), serverTime);
        }

        currentOrderId = model.orderId;
        currentCancelPath = cancelPath;
        if (els.title) els.title.textContent = model.title;
        if (els.productName) els.productName.textContent = model.productName;
        if (els.cashAmount) els.cashAmount.textContent = model.cashAmountText;
        if (els.payLink) els.payLink.href = model.paymentUrl;
        if (els.cancelBtn) els.cancelBtn.style.display = model.showCancelButton ? '' : 'none';

        // 出貨儲值單沒有保留窗口（決議 10），沒有倒數可算，整列連同數字一起藏起來
        const $remainingRow = els.remaining ? els.remaining.closest('.wtoy-draw-hold-row') : null;
        if ($remainingRow) {
            $remainingRow.style.display = model.hasCountdown ? '' : 'none';
        }

        if (model.hasCountdown) {
            const expiresAtMs = clock.now(Date.now()) + model.remainingSeconds * 1000;
            renderRemaining(expiresAtMs);
            stopCountdown();
            countdownTimer = setInterval(function () {
                renderRemaining(expiresAtMs);
            }, 1000);
        } else {
            stopCountdown();
        }

        root.classList.add('open');
    }

    if (els.closeBtn) {
        els.closeBtn.addEventListener('click', close);
    }
    if (els.cancelBtn) {
        els.cancelBtn.addEventListener('click', async function () {
            if (!currentOrderId) return;
            els.cancelBtn.disabled = true;
            try {
                const res = await requestCancel(restBase, nonce, currentOrderId, currentCancelPath);
                const outcome = cancelOutcome(res.success, res.data);
                if (outcome.action === 'cancelled') {
                    close();
                    if (typeof onCancelled === 'function') onCancelled(currentOrderId);
                } else if (outcome.action === 'poll') {
                    close();
                    if (typeof onPoll === 'function') onPoll(currentOrderId);
                } else {
                    alert(outcome.message || '取消失敗，請稍後再試。');
                }
            } finally {
                els.cancelBtn.disabled = false;
            }
        });
    }

    return { open, close };
}
